import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { hideGridTooltip, showGridTooltip } from './agGridTooltip'

const optionValue = (value) =>
  value && typeof value === 'object' && 'value' in value ? value.value : value

const toKey = (value) => String(optionValue(value))

const toLabel = (value) => {
  if (value && typeof value === 'object' && value.label != null) return String(value.label)
  const raw = optionValue(value)
  if (raw === true || raw === 'true') return 'Yes'
  if (raw === false || raw === 'false') return 'No'
  return raw == null || raw === '' ? '(Blank)' : String(raw)
}

const MENU_OPEN_EVENT = 'ag-custom-header-menu-open'

/**
 * Funnel filter header.
 * headerComponentParams: onSort, onSearch, getFilterValues(colId), getSelectedValues(colId)
 */
export default function CustomHeader(props) {
  const {
    displayName,
    column,
    api,
    setSort,
    enableSorting,
    enableFilter,
    enableFilterButton,
    onSort,
    onSearch,
    getFilterValues,
    getSelectedValues,
  } = props

  const colId = column?.getColId?.()
  const showFilter = !!(enableFilter || enableFilterButton || column?.getColDef?.()?.filter)
  const canSort = !!(enableSorting || column?.isSortable?.() || column?.getColDef?.()?.sortable)
  const options = typeof getFilterValues === 'function' ? (getFilterValues(colId) || []) : null
  const isSetFilter = Array.isArray(options)

  const [menuOpen, setMenuOpen] = useState(false)
  const [filterText, setFilterText] = useState('')
  const [searchText, setSearchText] = useState('')
  const [selected, setSelected] = useState([])
  const [menuStyle, setMenuStyle] = useState({})
  const buttonRef = useRef(null)
  const menuRef = useRef(null)

  const applied = typeof getSelectedValues === 'function' ? getSelectedValues(colId) : null
  const isFiltered = isSetFilter
    ? Array.isArray(applied) && applied.length > 0
    : !!filterText

  const visible = useMemo(() => {
    if (!isSetFilter) return []
    const q = searchText.trim().toLowerCase()
    if (!q) return options
    return options.filter(value => toLabel(value).toLowerCase().includes(q))
  }, [isSetFilter, options, searchText])

  useEffect(() => {
    if (!api || !colId || onSearch || isSetFilter) return undefined
    const sync = () => setFilterText(api.getColumnFilterModel(colId)?.filter ?? '')
    sync()
    api.addEventListener('filterChanged', sync)
    return () => api.removeEventListener('filterChanged', sync)
  }, [api, colId, onSearch, isSetFilter])

  useEffect(() => {
    if (!menuOpen || !isSetFilter) return
    setSelected(Array.isArray(applied) && applied.length ? applied.map(toKey) : options.map(toKey))
    setSearchText('')
  }, [menuOpen]) // eslint-disable-line react-hooks/exhaustive-deps -- sync only when menu opens

  useEffect(() => {
    if (!menuOpen) return undefined
    const onOutside = (event) => {
      if (menuRef.current?.contains(event.target) || buttonRef.current?.contains(event.target)) return
      setMenuOpen(false)
    }
    document.addEventListener('mousedown', onOutside)
    return () => document.removeEventListener('mousedown', onOutside)
  }, [menuOpen])

  useEffect(() => {
    const onOtherMenuOpen = (event) => {
      if (event.detail?.colId === colId) return
      setMenuOpen(false)
    }
    document.addEventListener(MENU_OPEN_EVENT, onOtherMenuOpen)
    return () => document.removeEventListener(MENU_OPEN_EVENT, onOtherMenuOpen)
  }, [colId])

  const emitSetFilter = (keys) => {
    setSelected(keys)
    if (!onSearch) return
    if (!keys.length || keys.length === options.length) {
      onSearch(null, colId)
      return
    }
    const byKey = Object.fromEntries(
      options.map((opt) => [toKey(opt), optionValue(opt)])
    )
    onSearch(keys.map((key) => byKey[key]), colId)
  }

  const applyTextFilter = (value) => {
    setFilterText(value)
    if (onSearch) {
      onSearch(value, colId)
      return
    }
    if (!api || !colId) return
    api.setColumnFilterModel(colId, value ? { filterType: 'text', type: 'contains', filter: value } : null)
      .then(() => api.onFilterChanged())
  }

  const toggleMenu = (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: Math.max(8, rect.right - 260),
      zIndex: 2000,
    })
    setMenuOpen((open) => {
      if (open) return false
      document.dispatchEvent(new CustomEvent(MENU_OPEN_EVENT, { detail: { colId } }))
      return true
    })
  }

  const handleSort = (direction) => {
    if (onSort) onSort(direction, colId)
    else if (setSort) setSort(direction)
    setMenuOpen(false)
  }

  const allVisibleSelected = visible.length > 0 && visible.every(value => selected.includes(toKey(value)))
  const someVisibleSelected = visible.some(value => selected.includes(toKey(value)))

  return (
    <div className="ag-cell-label-container ag-custom-header" role="presentation">
      <div className="ag-header-cell-label" role="presentation">
        <span
          className="ag-header-cell-text"
          onMouseEnter={event => displayName && showGridTooltip(displayName, event.currentTarget)}
          onMouseLeave={hideGridTooltip}
        >
          {displayName}
        </span>
      </div>

      {showFilter && (
        <button
          ref={buttonRef}
          type="button"
          className={[
            'ag-custom-header-filter-btn',
            menuOpen ? 'is-active' : '',
            isFiltered ? 'is-filtered text-primary' : '',
          ].filter(Boolean).join(' ')}
          onClick={toggleMenu}
          onMouseDown={event => event.stopPropagation()}
          title={isFiltered ? 'Filter applied' : 'Filter'}
        >
          <i className="la la-filter" />
          {isFiltered && <span className="ag-custom-header-filter-dot" aria-hidden="true" />}
        </button>
      )}

      {menuOpen && createPortal(
        <div ref={menuRef} className="ag-custom-header-menu" style={menuStyle}>
          {canSort && (
            <>
              <button type="button" className="ag-custom-header-menu-item" onClick={() => handleSort('asc')}>
                <i className="la la-arrow-up" />
                Sort ascending
              </button>
              <button type="button" className="ag-custom-header-menu-item" onClick={() => handleSort('desc')}>
                <i className="la la-arrow-down" />
                Sort descending
              </button>
              <div className="ag-custom-header-menu-separator" />
            </>
          )}
          <div className="ag-custom-header-menu-filter">
            <div className="ag-custom-header-menu-filter-label">{displayName}</div>
            {isSetFilter ? (
              <>
                <div className="ag-custom-header-set-search">
                  <i className="la la-search" aria-hidden="true" />
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search..."
                    value={searchText}
                    autoFocus
                    onChange={event => setSearchText(event.target.value)}
                    onClick={event => event.stopPropagation()}
                  />
                </div>
                <div className="ag-custom-header-set-list">
                  <label className={`ag-custom-header-set-option${allVisibleSelected ? ' is-checked' : ''}${!allVisibleSelected && someVisibleSelected ? ' is-indeterminate' : ''}`}>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      ref={(el) => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected }}
                      onChange={() => {
                        const visibleKeys = visible.map(toKey)
                        emitSetFilter(
                          allVisibleSelected
                            ? selected.filter(key => !visibleKeys.includes(key))
                            : Array.from(new Set([...selected, ...visibleKeys]))
                        )
                      }}
                    />
                    <span className="ag-custom-header-set-check" aria-hidden="true" />
                    <span>(Select All)</span>
                  </label>
                  {visible.length === 0 ? (
                    <div className="ag-custom-header-set-empty text-muted">No matches</div>
                  ) : visible.map((value) => {
                    const key = toKey(value)
                    const checked = selected.includes(key)
                    return (
                      <label key={key} className={`ag-custom-header-set-option${checked ? ' is-checked' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => emitSetFilter(
                            checked
                              ? selected.filter(item => item !== key)
                              : [...selected, key]
                          )}
                        />
                        <span className="ag-custom-header-set-check" aria-hidden="true" />
                        <span title={toLabel(value)}>{toLabel(value)}</span>
                      </label>
                    )
                  })}
                </div>
              </>
            ) : (
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Filter..."
                value={filterText}
                autoFocus
                onChange={event => applyTextFilter(event.target.value)}
                onClick={event => event.stopPropagation()}
              />
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
